// Loja de add-ons (Pro): configura o CARRINHO (pendente até pagar). +/− editam o
// carrinho na hora; o pagamento é único (checkout transparente), no botão "Pagar".
import { Sparkles, Smartphone, FolderKanban, HardDrive, Database, Minus, Plus, Loader2, ShoppingCart, Megaphone, CalendarClock, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';

export interface Entitlements {
  currency: string;
  plan: string;
  plan_monthly_cents: number;
  plan_renews_at: string;
  plan_cancel_at: string;
  recurring?: boolean;
  grace_until?: string;
  max_numbers_per_project: number; max_projects: number; storage_mb: number; message_retention_days: number;
  // Franquia do Free: interações do mês (enviadas + recebidas) e o teto incluído.
  messages_used?: number; message_free_count?: number;
  addon_numbers: number; addon_projects: number; addon_storage_gb: number; addon_retention_blocks: number;
  addon_number_cents: number; addon_project_cents: number; addon_storage_gb_cents: number; addon_retention_block_cents: number;
  // Add-ons de FEATURE (toggle).
  has_campaigns?: boolean; has_schedule_year?: boolean;
  addon_campaigns_cents?: number; addon_schedule_year_cents?: number;
}
export interface Cart {
  plan_pro: boolean; pro_monthly_cents: number;
  numbers: number; projects: number; storage_gb: number; retention_blocks: number;
  campaigns?: number; schedule_year?: number;
  prorated_cents: number; currency: string; empty: boolean;
}
export const CART_EMPTY: Cart = {
  plan_pro: false, pro_monthly_cents: 0, numbers: 0, projects: 0, storage_gb: 0,
  retention_blocks: 0, campaigns: 0, schedule_year: 0, prorated_cents: 0, currency: 'BRL', empty: true,
};

export function AddonStore({ e, cart, money, locale, busy, onChange, onPay, canPay }: {
  e: Entitlements; cart: Cart; money: (c: number) => string; locale: string;
  busy: string; onChange: (kind: string, delta: number) => void; onPay: () => void; canPay: boolean;
}) {
  const { t } = useI18n();
  const daysLeft = e.plan_renews_at
    ? Math.max(1, Math.ceil((new Date(e.plan_renews_at).getTime() - Date.now()) / 86_400_000))
    : 30;
  const prorate = (unit: number) => Math.min(unit, Math.ceil((unit * daysLeft) / 30));

  const storageGb = (e.storage_mb / 1024).toLocaleString(locale, { maximumFractionDigits: 1 });
  const rows = [
    { kind: 'number', icon: Smartphone, label: t('billing.addonNumbers'), unit: e.addon_number_cents, active: e.addon_numbers, pending: cart.numbers, sub: t('billing.addonNumbersSub', { active: String(e.max_numbers_per_project) }) },
    { kind: 'project', icon: FolderKanban, label: t('billing.addonProjects'), unit: e.addon_project_cents, active: e.addon_projects, pending: cart.projects, sub: t('billing.addonProjectsSub', { active: String(e.max_projects) }) },
    { kind: 'storage_gb', icon: HardDrive, label: t('billing.addonStorage'), unit: e.addon_storage_gb_cents, active: e.addon_storage_gb, pending: cart.storage_gb, sub: t('billing.addonStorageSub', { active: storageGb }) },
    { kind: 'retention_block', icon: Database, label: t('billing.addonRetention'), unit: e.addon_retention_block_cents, active: e.addon_retention_blocks, pending: cart.retention_blocks, sub: t('billing.addonRetentionSub', { active: String(e.message_retention_days) }) },
  ];

  // Add-ons de FEATURE (toggle on/off): campanhas e agendamento estendido.
  const features = [
    {
      kind: 'campaigns', icon: Megaphone, label: 'Campanhas',
      unit: e.addon_campaigns_cents ?? 0, on: !!e.has_campaigns, pending: cart.campaigns ?? 0,
      sub: 'Envio para listas com opt-in: opt-out automático, limites por número e pausa quando a entrega falha.',
    },
    {
      kind: 'schedule_year', icon: CalendarClock, label: 'Agendamento estendido',
      unit: e.addon_schedule_year_cents ?? 0, on: !!e.has_schedule_year, pending: cart.schedule_year ?? 0,
      sub: 'Agende envios com até 1 ano de antecedência (sem o add-on: Pro vai até 30 dias).',
    },
  ];

  return (
    <Card className="border-primary/30">
      <CardHeader className="flex-row items-center gap-2"><Sparkles className="size-5 text-primary" /><CardTitle>{t('billing.addonsTitle')}</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">{t('billing.addonsIntro')}</p>
        {rows.map((row) => {
          const Icon = row.icon;
          const total = row.active + row.pending;
          return (
            <div key={row.kind} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${row.pending > 0 ? 'border-amber-400 bg-amber-50' : 'bg-background'}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Icon className="size-5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">{row.label}</p>
                  <p className="text-xs text-muted-foreground">{t('billing.addonUnit', { price: money(row.unit) })} · {row.sub}</p>
                  {row.pending > 0
                    ? <p className="text-[0.72rem] font-medium text-amber-700">{t('billing.addonInCart', { n: String(row.pending), amount: money(prorate(row.unit) * row.pending) })}</p>
                    : <p className="text-[0.7rem] text-muted-foreground">{t('billing.addonProrate', { amount: money(prorate(row.unit)) })}</p>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" size="icon" className="size-8" title={t('billing.addonRemove')} disabled={!!busy || total <= 0} onClick={() => onChange(row.kind, -1)}>
                  {busy === `${row.kind}:rm` ? <Loader2 className="size-4 animate-spin" /> : <Minus className="size-4" />}
                </Button>
                <span className="w-10 text-center text-lg font-bold tabular-nums">
                  {row.active}{row.pending > 0 && <span className="text-sm font-medium text-amber-600">+{row.pending}</span>}
                </span>
                <Button variant="outline" size="icon" className="size-8" title={t('billing.addonAdd')} disabled={!!busy} onClick={() => onChange(row.kind, 1)}>
                  {busy === `${row.kind}:add` ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                </Button>
              </div>
            </div>
          );
        })}
        {/* Recursos (add-ons de feature) — toggle on/off */}
        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recursos</p>
        {features.map((f) => {
          const Icon = f.icon;
          const enabled = f.on || f.pending > 0; // já ativo ou pendente no carrinho
          return (
            <div key={f.kind} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${f.pending > 0 ? 'border-amber-400 bg-amber-50' : 'bg-background'}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Icon className="size-5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">{f.label}</p>
                  <p className="text-xs text-muted-foreground">{t('billing.addonUnit', { price: money(f.unit) })} · {f.sub}</p>
                  {f.pending > 0 && <p className="text-[0.72rem] font-medium text-amber-700">No carrinho · {money(prorate(f.unit))}</p>}
                  {f.on && f.pending === 0 && <p className="text-[0.72rem] font-medium text-emerald-600">Ativo no seu plano</p>}
                </div>
              </div>
              <Button
                variant={enabled ? 'default' : 'outline'}
                size="sm"
                className="shrink-0"
                disabled={!!busy}
                onClick={() => onChange(f.kind, enabled ? -1 : 1)}
              >
                {busy === `${f.kind}:add` || busy === `${f.kind}:rm`
                  ? <Loader2 className="size-4 animate-spin" />
                  : enabled ? <Check className="size-4" /> : <Plus className="size-4" />}
                {enabled ? 'Ativado' : 'Ativar'}
              </Button>
            </div>
          );
        })}

        {canPay && (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-primary/5 p-3">
            <div>
              <p className="text-sm font-medium">{t('billing.cartTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('billing.cartTotal', { amount: money(cart.prorated_cents) })}</p>
            </div>
            <Button disabled={!!busy} onClick={onPay}>
              {busy === 'pay' ? <Loader2 className="size-4 animate-spin" /> : <ShoppingCart className="size-4" />} {t('billing.checkoutPay', { amount: money(cart.prorated_cents) })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
